/** Keyboard + mouse state, wired to the canvas. */
export class Input {
  private readonly down = new Set<string>();
  /** Codes pressed since the last `endFrame()` — used for edge-triggered keys. */
  private readonly pressed = new Set<string>();
  readonly mouse = { dx: 0, dy: 0, left: false, right: false, middle: false, wheel: 0 };
  private readonly mousePressed = { left: false, right: false, middle: false };

  locked = false;
  private readonly target: HTMLElement;

  /** Called when pointer lock is gained or lost. */
  onLockChange: ((locked: boolean) => void) | null = null;

  constructor(target: HTMLElement) {
    this.target = target;
    this.bind();
  }

  /** Detach every listener so a torn-down game leaves nothing behind. */
  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    window.removeEventListener("mouseup", this.onMouseUp);
    document.removeEventListener("pointerlockchange", this.onLockChanged);
    this.target.removeEventListener("mousemove", this.onMouseMove);
    this.target.removeEventListener("mousedown", this.onMouseDown);
    this.target.removeEventListener("contextmenu", this.onContextMenu);
    this.target.removeEventListener("wheel", this.onWheel);
    if (document.pointerLockElement === this.target) document.exitPointerLock();
  }

  private bind(): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    window.addEventListener("mouseup", this.onMouseUp);
    document.addEventListener("pointerlockchange", this.onLockChanged);
    this.target.addEventListener("mousemove", this.onMouseMove);
    this.target.addEventListener("mousedown", this.onMouseDown);
    this.target.addEventListener("contextmenu", this.onContextMenu);
    this.target.addEventListener("wheel", this.onWheel, { passive: false });
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Don't swallow devtools / reload shortcuts.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (!this.down.has(e.code)) this.pressed.add(e.code);
    this.down.add(e.code);
    if (PREVENT_DEFAULT_KEYS.has(e.code)) e.preventDefault();
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.down.delete(e.code);
  };

  // Losing focus mid-sprint would otherwise leave keys stuck down.
  private readonly onBlur = (): void => {
    this.clearHeld();
  };

  private readonly onLockChanged = (): void => {
    this.locked = document.pointerLockElement === this.target;
    if (!this.locked) this.clearHeld();
    this.onLockChange?.(this.locked);
  };

  private readonly onMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    this.mouse.dx += e.movementX;
    this.mouse.dy += e.movementY;
  };

  private readonly onMouseDown = (e: MouseEvent): void => {
    if (!this.locked) return;
    e.preventDefault();
    if (e.button === 0) {
      this.mouse.left = true;
      this.mousePressed.left = true;
    }
    if (e.button === 2) {
      this.mouse.right = true;
      this.mousePressed.right = true;
    }
    if (e.button === 1) {
      this.mouse.middle = true;
      this.mousePressed.middle = true;
    }
  };

  private readonly onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouse.left = false;
    if (e.button === 2) this.mouse.right = false;
    if (e.button === 1) this.mouse.middle = false;
  };

  private readonly onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  private readonly onWheel = (e: WheelEvent): void => {
    if (!this.locked) return;
    e.preventDefault();
    this.mouse.wheel += Math.sign(e.deltaY);
  };

  private clearHeld(): void {
    this.down.clear();
    this.mouse.left = false;
    this.mouse.right = false;
    this.mouse.middle = false;
  }

  requestLock(): void {
    if (document.pointerLockElement === this.target) return;
    try {
      // Chrome returns a promise and rejects when asked to relock too soon
      // after an unlock; swallow that so it never surfaces as an unhandled
      // rejection. Older browsers may throw synchronously instead.
      const result = this.target.requestPointerLock() as unknown as
        | Promise<void>
        | undefined;
      if (result && typeof result.catch === "function") result.catch(() => {});
    } catch {
      // No user activation or a lock cooldown: the canvas click retries later.
    }
  }

  isDown(code: string): boolean {
    return this.down.has(code);
  }

  /** True once per physical key press. */
  wasPressed(code: string): boolean {
    return this.pressed.has(code);
  }

  wasLeftClicked(): boolean {
    return this.mousePressed.left;
  }

  wasRightClicked(): boolean {
    return this.mousePressed.right;
  }

  wasMiddleClicked(): boolean {
    return this.mousePressed.middle;
  }

  /** Consume per-frame state. Call at the very end of the update loop. */
  endFrame(): void {
    this.pressed.clear();
    this.mouse.dx = 0;
    this.mouse.dy = 0;
    this.mouse.wheel = 0;
    this.mousePressed.left = false;
    this.mousePressed.right = false;
    this.mousePressed.middle = false;
  }
}

/** Keys whose browser default (scrolling, quick-find) would break gameplay. */
const PREVENT_DEFAULT_KEYS = new Set([
  "Space",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Tab",
  "F3",
  "F5",
  "Slash",
]);
